import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  User, 
  Briefcase, 
  Building, 
  Calendar, 
  Link, 
  Users, 
  FileText,
  Clock,
  CheckSquare2,
  CheckSquare,
  Square
} from "lucide-react";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUpdateTask } from "@/hooks/tasks/useTasks";

interface TaskInfoPopoverProps {
  task: any;
  onTaskUpdated?: () => void;
}

interface LinkedEntity {
  id: string;
  name: string;
  type: string;
  additionalInfo?: string;
}

export const TaskInfoPopover = ({ task, onTaskUpdated }: TaskInfoPopoverProps) => {
  const { t } = useTranslation();
  const [linkedEntities, setLinkedEntities] = useState<LinkedEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTaskMutation = useUpdateTask();

  useEffect(() => {
    const loadLinkedEntities = async () => {
      if (!task.relations) {
        setIsLoading(false);
        return;
      }

      const entities: LinkedEntity[] = [];

      try {
        // Load candidates
        if (task.relations.candidates && task.relations.candidates.length > 0) {
          const { data: candidates } = await supabase
            .from('candidates')
            .select('id, name, email')
            .in('id', task.relations.candidates);
          
          if (candidates) {
            candidates.forEach(candidate => {
              entities.push({
                id: candidate.id,
                name: candidate.name,
                type: 'candidate',
                additionalInfo: candidate.email
              });
            });
          }
        }

        // Load jobs
        if (task.relations.jobs && task.relations.jobs.length > 0) {
          const { data: jobs } = await supabase
            .from('jobs')
            .select('id, title, companies(name)')
            .in('id', task.relations.jobs);
          
          if (jobs) {
            jobs.forEach(job => {
              entities.push({
                id: job.id,
                name: job.title,
                type: 'job',
                additionalInfo: job.companies?.name
              });
            });
          }
        }

        // Load companies
        if (task.relations.companies && task.relations.companies.length > 0) {
          const { data: companies } = await supabase
            .from('companies')
            .select('id, name, city, country')
            .in('id', task.relations.companies);
          
          if (companies) {
            companies.forEach(company => {
              entities.push({
                id: company.id,
                name: company.name,
                type: 'company',
                additionalInfo: `${company.city}, ${company.country}`
              });
            });
          }
        }

        // Load matches
        if (task.relations.matches && task.relations.matches.length > 0) {
          const { data: matches } = await supabase
            .from('matches')
            .select(`
              id,
              candidates(name),
              jobs(title, companies(name))
            `)
            .in('id', task.relations.matches);
          
          if (matches) {
            matches.forEach(match => {
              entities.push({
                id: match.id,
                name: `${match.candidates?.name || 'Unknown'} ↔ ${match.jobs?.title || 'Unknown'}`,
                type: 'match',
                additionalInfo: match.jobs?.companies?.name
              });
            });
          }
        }

        // Load linked users
        if (task.linked_users && task.linked_users.length > 0) {
          task.linked_users.forEach(user => {
            entities.push({
              id: user.user_id,
              name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User',
              type: 'user',
              additionalInfo: user.email || undefined
            });
          });
        }

        setLinkedEntities(entities);
      } catch (error) {
        console.error('Error loading linked entities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedEntities();
  }, [task.relations]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      todo: { variant: "secondary" as const, label: t("tasks.status.todo", "To Do") },
      inProgress: { variant: "default" as const, label: t("tasks.status.inProgress", "In Progress") },
      done: { variant: "success" as const, label: t("tasks.status.done", "Done") },
      cancelled: { variant: "destructive" as const, label: t("tasks.status.cancelled", "Cancelled") }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.todo;
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: "secondary" as const, label: t("tasks.priority.low", "Low") },
      medium: { variant: "default" as const, label: t("tasks.priority.medium", "Medium") },
      high: { variant: "warning" as const, label: t("tasks.priority.high", "High") },
      urgent: { variant: "destructive" as const, label: t("tasks.priority.urgent", "Urgent") }
    };
    
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>;
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'candidate':
        return <User className="h-3 w-3" />;
      case 'job':
        return <Briefcase className="h-3 w-3" />;
      case 'company':
        return <Building className="h-3 w-3" />;
      case 'match':
        return <Link className="h-3 w-3" />;
      case 'user':
        return <Users className="h-3 w-3" />;
      default:
        return <FileText className="h-3 w-3" />;
    }
  };

  const getEntityColor = (type: string) => {
    switch (type) {
      case 'candidate':
        return 'text-blue-500 bg-blue-50';
      case 'job':
        return 'text-green-600 bg-green-50';
      case 'company':
        return 'text-purple-600 bg-purple-50';
      case 'match':
        return 'text-orange-600 bg-orange-50';
      case 'user':
        return 'text-indigo-600 bg-indigo-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleToggleStatus = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      const newStatus = task.status === 'done' ? 'todo' : 'done';
      
      await updateTaskMutation.mutateAsync({
        id: task.id,
        status: newStatus
      });

      // Update the task object locally
      task.status = newStatus;
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const isDone = task.status === 'done' || task.status === 'cancelled';
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';

  return (
    <div className="max-h-[28rem] overflow-y-auto">
      <div className="p-4 w-full">
        {/* Header */}
                  <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-sm flex items-center justify-center flex-shrink-0">
              <CheckSquare2 className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={handleToggleStatus}
                  disabled={isUpdating}
                  className={`p-1 rounded transition-colors ${
                    isDone 
                      ? 'text-green-600 hover:text-green-700' 
                      : 'text-gray-400 hover:text-gray-600'
                  } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  title={isDone ? t("tasks.actions.markUndone", "Mark as undone") : t("tasks.actions.markDone", "Mark as done")}
                >
                  {isDone ? (
                    <CheckSquare className="h-4 w-4" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>
                <h3 className={`font-semibold text-sm break-words ${isDone ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                  {task.name}
                </h3>
              </div>
              <p className="text-xs text-gray-500">{t("tasks.info.taskDetails", "Task Details")}</p>
            </div>
          </div>

      {task.description && (
        <>
          <Separator className="mb-4" />
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-3 w-3 text-gray-400" />
              <span className="text-xs font-medium text-gray-700">{t("tasks.info.description", "Description")}</span>
            </div>
            <p 
              className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: task.description }}
            />
          </div>
        </>
      )}

      <Separator className="mb-4" />

        {/* Task Info */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-600 flex-shrink-0">{t("tasks.info.status", "Status")}:</span>
            {getStatusBadge(task.status)}
          </div>
          
          <div className="flex items-center gap-2">
            <FileText className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-600 flex-shrink-0">{t("tasks.info.priority", "Priority")}:</span>
            {getPriorityBadge(task.priority)}
          </div>

          {task.due_date && (
            <div className="flex items-center gap-2">
              <Calendar className={`h-3 w-3 flex-shrink-0 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`} />
              <span className="text-xs text-gray-600 flex-shrink-0">{t("tasks.info.dueDate", "Due")}:</span>
              <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                {format(new Date(task.due_date), "MMM dd, yyyy")}
                {isOverdue && (
                  <span className="ml-1 text-red-500">({t("tasks.info.overdue", "Overdue")})</span>
                )}
              </span>
            </div>
          )}

          {task.assignee && (
            <div className="flex items-center gap-2">
              <Users className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-600 flex-shrink-0">{t("tasks.info.assignedTo", "Assigned to")}:</span>
              <span className="text-xs font-medium break-words">{task.assignee.first_name} {task.assignee.last_name}</span>
            </div>
          )}
        </div>

        <Separator className="mb-4" />

        {/* Linked Entities */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Link className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs font-medium text-gray-700">{t("tasks.info.linkedElements", "Linked Elements")}</span>
            <Badge variant="outline" className="text-xs">{linkedEntities.length}</Badge>
          </div>

          {isLoading ? (
            <div className="text-xs text-gray-500 text-center py-2">
              {t("tasks.info.loading", "Loading...")}
            </div>
          ) : linkedEntities.length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-2">
              {t("tasks.info.noLinkedElements", "No linked elements")}
            </div>
          ) : (
            <div className="space-y-2">
              {linkedEntities.map((entity) => (
                <Card key={entity.id} className="p-2 border-0 bg-gray-50">
                  <CardContent className="p-0">
                    <div className="flex items-center gap-2">
                      <div className={`p-1 rounded ${getEntityColor(entity.type)}`}>
                        {getEntityIcon(entity.type)}
                      </div>
                                           <div className="flex-1 min-w-0">
                       <p className="text-xs font-medium text-gray-900 break-words">{entity.name}</p>
                       {entity.additionalInfo && (
                         <p className="text-xs text-gray-500 break-words">{entity.additionalInfo}</p>
                       )}
                     </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
